import { css } from "@fluentui/react";
import { IMenuItem, MenuButton } from "azure-devops-ui/Menu";
import { Location } from "azure-devops-ui/Utilities/Position";
import React from "react";
import { lazy } from "../../../common/components/lazy/lazy";
import { Observer } from "../../../common/components/observer/observer";
import { ResponsiveLayout } from "../../../common/components/responsivelayout/responsivelayout";
import { Scenario } from "../../../common/components/scenario/scenario";
import { EventContext } from "../../../common/contexts/event";
import { useObservable } from "../../../common/hooks/useobservable";
import { IEventDispatch } from "../../../common/utilities/dispatch";
import { IDownloadOptions, ITagApi } from "../../api/photo";
import { SessionContext } from "../../contexts/session";
import { IItem } from "../../types/item";
import { IPhoto, ITag } from "../../types/photo";
import { getDuplicatedPhotosParentFromPhoto } from "../../utilities/util";
import { Delete, InformationInverted, Share } from "../illustration/icons";
import { IPhotoTilePhoto } from "../phototile/phototile";
import { DeleteQuickCommand, DetailsQuickCommand, FavoriteQuickCommand, ShareQuickCommand } from "../quickcommand/quickcommand";

import "./quickactions.css";

const DeletePhotoDialog = lazy(() => import("../../dialogs/deletephotodialog"));
const DetailsPanel = lazy(() => import("../../panels/detailspanel"));
const ShareDialog = lazy(() => import("../../dialogs/sharedialog"));

const { DeleteButton, MoreButton, InfoButton, ShareButton } = window.Resources.Common;

export interface IOverlayCommands {
  share?: {};

  delete?: {
    deletePhotos: (eventDispatch: IEventDispatch, driveId: string, photoIds: string[]) => Promise<PromiseSettledResult<void>[]>;
  };

  info?: {
    addTag: (eventDispatch: IEventDispatch, tagApi: ITagApi, driveId: string, photoId: string, tag: string) => Promise<void>;
    deleteTag: (eventDispatch: IEventDispatch, tagApi: ITagApi, driveId: string, photoId: string, tag: ITag) => Promise<void>;
    downloadPhoto: (
      fetch: (url: string, init?: RequestInit) => Promise<Response>,
      driveId: string,
      photoId: string,
      options?: IDownloadOptions
    ) => Promise<Blob>;
    getItem?: (driveId: string, itemId: string) => Promise<IItem>;
    updatePhoto: (eventDispatch: IEventDispatch, driveId: string, photoId: string, photo: Partial<Exclude<IPhoto, "id">>) => Promise<IPhoto>;
  };

  favorite?: {
    addFavorite: (eventDispatch: IEventDispatch, photoIds: string[]) => Promise<void>;
    removeFavorite: (eventDispatch: IEventDispatch, photoIds: string[]) => Promise<void>;
  };
}

/**
 * The QuickActions component offers an interface that allows the caller to
 * determine which action if any is currently active.
 */
export interface IQuickActions {
  commandActive: () => "delete" | "details" | "share" | undefined;
}

export interface IQuickActionsProps {
  /**
   * Set of command options to be used in order to perform the quick actions
   */
  commands: IOverlayCommands;

  /**
   * Reference to the QuickActions API interface.
   */
  componentRef?: React.MutableRefObject<IQuickActions | undefined>;

  /**
   * Photo the quick commands belong to
   */
  photo: IPhotoTilePhoto;

  /**
   * Allow the caller to supply simple name value pairs as additional data that
   * can be included in telemetry events sourced by the photo tile.
   */
  telemetryProperties?: { [propertyName: string]: string | number | boolean | undefined | null };
}

/**
 * Component to show quick action commands on the overlay
 * @param props IOverlayCommandBarProps
 * @returns
 */
export function QuickActions(props: IQuickActionsProps): React.ReactElement {
  const { commands, componentRef, photo, telemetryProperties } = props;

  const eventContext = React.useContext(EventContext);
  const sessionContext = React.useContext(SessionContext);

  const commandElement = React.useRef<HTMLDivElement>(null);

  const [, setCount] = React.useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useObservable(false);
  const [showDetailsPanel, setShowDetailsPanel] = useObservable(false);
  const [showShareDialog, setShowShareDialog] = useObservable(false);

  React.useImperativeHandle(componentRef, () => ({
    commandActive: () => (showDeleteDialog.value ? "delete" : showDetailsPanel.value ? "details" : showShareDialog.value ? "share" : undefined)
  }));

  const shareCommand = commands.share ? <ShareQuickCommand key="share" onClick={() => setShowShareDialog(true)} /> : undefined;
  const deleteCommand = commands.delete ? <DeleteQuickCommand key="download" onClick={() => setShowDeleteDialog(true)} photo={photo} /> : undefined;
  const infoCommand = commands.info ? <DetailsQuickCommand key="details" onClick={() => setShowDetailsPanel(true)} photo={photo} /> : undefined;
  const favoriteCommand =
    commands.favorite && !sessionContext.migrated ? (
      <FavoriteQuickCommand
        key="favorite"
        onClick={(event) => {
          eventContext.dispatchEvent("telemetryAvailable", {
            ...telemetryProperties,
            action: "userAction",
            name: "favoriteQuickAction"
          });

          photo.viewpoint?.isFavorite
            ? commands.favorite!.removeFavorite(eventContext, [photo.id])
            : commands.favorite!.addFavorite(eventContext, [photo.id]);
          setCount((count) => count + 1);
          event.preventDefault();
        }}
        photo={photo}
      />
    ) : undefined;

  const menuItems: IMenuItem[] = [];

  if (commands.share && shareCommand) {
    menuItems.push({
      className: "phototile-command",
      id: "phototiie-share-command",
      iconProps: { render: (className: string) => <Share className={className} /> },
      text: ShareButton,
      onActivate: () => {
        eventContext.dispatchEvent("telemetryAvailable", {
          ...telemetryProperties,
          action: "userAction",
          name: "shareQuickAction"
        });

        setShowShareDialog(true);
      }
    });
  }

  if (commands.delete && deleteCommand) {
    menuItems.push({
      className: "phototile-command",
      id: "phototiie-delete-command",
      iconProps: { render: (className: string) => <Delete className={className} /> },
      text: DeleteButton,
      onActivate: () => {
        eventContext.dispatchEvent("telemetryAvailable", {
          ...telemetryProperties,
          action: "userAction",
          name: "deleteQuickAction"
        });

        setShowDeleteDialog(true);
      }
    });
  }

  if (commands.info && infoCommand) {
    menuItems.push({
      className: "phototile-command",
      id: "phototiie-info-command",
      iconProps: { render: (className: string) => <InformationInverted className={className} /> },
      text: InfoButton,
      onActivate: () => {
        eventContext.dispatchEvent("telemetryAvailable", {
          ...telemetryProperties,
          action: "userAction",
          name: "detailsQuickAction"
        });

        setShowDetailsPanel(true);
      }
    });
  }

  return (
    <ResponsiveLayout containerElement={commandElement} key="command-layout" responsiveSelectors={[".quick-command-container"]}>
      {({ responsiveIndex }) => (
        <div className="flex-row flex-justify-between flex-grow overflow-hidden padding-4 rhythm-horizontal-4" ref={commandElement} role="group">
          <div className="favorite-quick-action tile-hover flex-row rounded pointer-events-auto">{favoriteCommand}</div>
          <div className="quick-command-container flex-row flex-align-center flex-noshrink pointer-events-auto">
            {shareCommand}
            {deleteCommand}
            {infoCommand}
          </div>

          <MenuButton
            ariaLabel={MoreButton}
            className={css(
              "transparent icon-only tile-hover flex-row rounded pointer-events-auto flex-noshrink margin-left-8",
              responsiveIndex === -1 && "hidden"
            )}
            contextualMenuProps={{
              anchorOffset: { horizontal: 0, vertical: 4 },
              anchorOrigin: { horizontal: Location.start, vertical: Location.end },
              className: "phototile-dropdown compact-menu",
              menuOrigin: { horizontal: Location.start, vertical: Location.start },
              menuProps: {
                id: "overflow-menu",
                items: menuItems
              }
            }}
            excludeFocusZone={true}
            hideDropdownIcon={true}
            iconProps={{ iconName: "More", className: "more-quick-action" }}
            subtle={true}
          />
          <Observer values={{ showDeleteDialog }}>
            {({ showDeleteDialog }) =>
              showDeleteDialog && commands.delete ? (
                <React.Suspense fallback={null}>
                  <Scenario scenarioName="deletephotos">
                    <DeletePhotoDialog
                      deletePhotos={commands.delete.deletePhotos}
                      duplicatePhotoParentName={getDuplicatedPhotosParentFromPhoto(photo)}
                      onDismiss={() => {
                        setShowDeleteDialog(false);
                      }}
                      photoIds={[photo.id, ...(photo.duplicates?.map((duplicatedPhoto) => duplicatedPhoto.id) ?? [])]}
                    />
                  </Scenario>
                </React.Suspense>
              ) : null
            }
          </Observer>
          <Observer values={{ showShareDialog }}>
            {({ showShareDialog }) =>
              showShareDialog ? (
                <React.Suspense fallback={null}>
                  <Scenario scenarioName="sharedialog">
                    <ShareDialog
                      items={[photo]}
                      onDismiss={() => {
                        setShowShareDialog(false);
                      }}
                    />
                  </Scenario>
                </React.Suspense>
              ) : null
            }
          </Observer>
          <Observer values={{ showDetailsPanel }}>
            {({ showDetailsPanel }) =>
              showDetailsPanel && commands.info ? (
                <React.Suspense fallback={null}>
                  <Scenario scenarioName="detailspanel">
                    <DetailsPanel
                      addTag={commands.info.addTag}
                      deleteTag={commands.info.deleteTag}
                      downloadPhoto={commands.info.downloadPhoto}
                      getItem={commands.info.getItem}
                      onDismiss={() => setShowDetailsPanel(false)}
                      photo={photo}
                      updatePhoto={commands.info.updatePhoto}
                    />
                  </Scenario>
                </React.Suspense>
              ) : null
            }
          </Observer>
        </div>
      )}
    </ResponsiveLayout>
  );
}
